/**
 * URL and IP checks for server-side requests to user-supplied URLs. Everything
 * here is pure and runs on Cloudflare Workers and Bun alike. The DNS-resolving,
 * connection-pinning fetch lives in `@inference-net/ssrf-guard/fetch` (Bun only).
 */

const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const HEXTET_PATTERN = /^[0-9a-f]{1,4}$/i;

/**
 * Why a guarded request did not happen:
 * - `policy`: the URL or an address it resolves to is not allowed. Retrying
 *   the same URL cannot succeed.
 * - `network`: DNS or the connection failed. A later retry may succeed.
 */
export type BlockedFetchTargetKind = "policy" | "network";

/** Raised when a URL is rejected before or during the fetch. */
export class BlockedFetchTargetError extends Error {
  readonly kind: BlockedFetchTargetKind;

  constructor(
    message: string,
    options: { kind: BlockedFetchTargetKind; cause?: unknown },
  ) {
    super(message, { cause: options.cause });
    this.name = "BlockedFetchTargetError";
    this.kind = options.kind;
  }
}

function parseIpv4Octets(address: string): number[] | null {
  const match = IPV4_PATTERN.exec(address);
  if (match == null) {
    return null;
  }

  const octets = match.slice(1).map(Number);
  if (octets.some((octet) => Number.isNaN(octet) || octet > 255)) {
    return null;
  }

  return octets;
}

function isBlockedIpv4(octets: number[]): boolean {
  const [first, second, third] = octets;
  if (first == null || second == null || third == null) {
    return true;
  }

  return (
    first === 0 || // 0.0.0.0/8 "this network"
    first === 10 || // RFC1918
    first === 127 || // loopback
    (first === 100 && second >= 64 && second <= 127) || // CGNAT 100.64/10
    (first === 169 && second === 254) || // link-local, incl. cloud metadata
    (first === 172 && second >= 16 && second <= 31) || // RFC1918
    (first === 192 && second === 168) || // RFC1918
    (first === 192 && second === 0 && third === 0) || // IETF protocol assignments
    (first === 192 && second === 0 && third === 2) || // TEST-NET-1
    (first === 192 && second === 88 && third === 99) || // 6to4 relay anycast
    (first === 198 && (second === 18 || second === 19)) || // benchmarking
    (first === 198 && second === 51 && third === 100) || // TEST-NET-2
    (first === 203 && second === 0 && third === 113) || // TEST-NET-3
    first >= 224 // multicast, reserved, broadcast
  );
}

/**
 * Splits an IPv6 address into its 8 hextets, expanding `::` and any trailing
 * dotted-quad. Returns null when the address is not valid IPv6.
 */
function parseIpv6Hextets(address: string): number[] | null {
  const withoutZone = address.split("%")[0] ?? "";
  if (!withoutZone.includes(":")) {
    return null;
  }

  const sides = withoutZone.split("::");
  if (sides.length > 2) {
    return null;
  }

  const expandSide = (side: string): number[] | null => {
    if (side === "") {
      return [];
    }

    const pieces = side.split(":");
    const hextets: number[] = [];
    for (const [index, piece] of pieces.entries()) {
      // A trailing dotted-quad (`::ffff:169.254.169.254`) fills two hextets.
      if (index === pieces.length - 1 && piece.includes(".")) {
        const octets = parseIpv4Octets(piece);
        if (octets == null) {
          return null;
        }
        hextets.push(
          ((octets[0] ?? 0) << 8) | (octets[1] ?? 0),
          ((octets[2] ?? 0) << 8) | (octets[3] ?? 0),
        );
        continue;
      }

      if (!HEXTET_PATTERN.test(piece)) {
        return null;
      }
      hextets.push(Number.parseInt(piece, 16));
    }

    return hextets;
  };

  const head = expandSide(sides[0] ?? "");
  const tail = sides.length === 2 ? expandSide(sides[1] ?? "") : [];
  if (head == null || tail == null) {
    return null;
  }

  if (sides.length === 1) {
    return head.length === 8 ? head : null;
  }

  const elidedCount = 8 - head.length - tail.length;
  if (elidedCount < 1) {
    return null;
  }

  return [...head, ...new Array<number>(elidedCount).fill(0), ...tail];
}

/**
 * Returns the IPv4 address an IPv6 address carries, so those forms are judged
 * by the address they actually reach:
 * - low 32 bits of IPv4-mapped (`::ffff:a.b.c.d`), IPv4-compatible
 *   (`::a.b.c.d`), IPv4-translated (`::ffff:0:a.b.c.d`) and NAT64
 *   (`64:ff9b::/96`) addresses;
 * - bits 16-47 of a 6to4 address (`2002:aabb:ccdd::/48`).
 */
function extractEmbeddedIpv4(hextets: number[]): number[] | null {
  const toOctets = (high: number, low: number): number[] => [
    high >> 8,
    high & 0xff,
    low >> 8,
    low & 0xff,
  ];

  if (hextets[0] === 0x2002) {
    return toOctets(hextets[1] ?? 0, hextets[2] ?? 0);
  }

  const isMappedOrCompatible =
    hextets.slice(0, 5).every((hextet) => hextet === 0) &&
    (hextets[5] === 0xffff || hextets[5] === 0);
  const isTranslated =
    hextets.slice(0, 4).every((hextet) => hextet === 0) &&
    hextets[4] === 0xffff &&
    hextets[5] === 0;
  const isNat64 =
    hextets[0] === 0x0064 &&
    hextets[1] === 0xff9b &&
    hextets.slice(2, 6).every((hextet) => hextet === 0);

  if (!isMappedOrCompatible && !isTranslated && !isNat64) {
    return null;
  }

  return toOctets(hextets[6] ?? 0, hextets[7] ?? 0);
}

function isBlockedIpv6(hextets: number[]): boolean {
  const embeddedIpv4 = extractEmbeddedIpv4(hextets);
  if (embeddedIpv4 != null) {
    // `::` and `::1` land here as 0.0.0.0 and 0.0.0.1, both inside 0.0.0.0/8.
    return isBlockedIpv4(embeddedIpv4);
  }

  const [first = 0, second = 0, third = 0, fourth = 0] = hextets;
  return (
    (first === 0x0100 && second === 0 && third === 0 && fourth === 0) || // 100::/64 discard
    (first === 0x2001 && second === 0) || // 2001::/32 Teredo, embeds an obfuscated IPv4
    (first === 0x2001 && second === 0x0db8) || // 2001:db8::/32 documentation
    (first === 0x0064 && second === 0xff9b && third === 0x0001) || // 64:ff9b:1::/48 local-use NAT64
    (first & 0xffc0) === 0xfec0 || // fec0::/10 deprecated site-local
    (first & 0xffc0) === 0xfe80 || // fe80::/10 link-local
    (first & 0xfe00) === 0xfc00 || // fc00::/7 unique local
    (first & 0xff00) === 0xff00 // ff00::/8 multicast
  );
}

/**
 * True when the address is loopback, private, link-local, CGNAT, ULA, multicast
 * or otherwise not a public internet destination. Unparseable input is blocked.
 */
export function isBlockedIpAddress(address: string): boolean {
  const octets = parseIpv4Octets(address);
  if (octets != null) {
    return isBlockedIpv4(octets);
  }

  const hextets = parseIpv6Hextets(address);
  if (hextets != null) {
    return isBlockedIpv6(hextets);
  }

  return true;
}

/** URL hostnames keep IPv6 literals bracketed; DNS and TLS want them bare. */
export function normalizeHostname(hostname: string): string {
  const lowercased = hostname.toLowerCase();
  return lowercased.startsWith("[") && lowercased.endsWith("]")
    ? lowercased.slice(1, -1)
    : lowercased;
}

/**
 * Checks the parts of a URL that need no network access. Kept separate from the
 * resolved-IP check so callers can reject malformed input with a precise
 * message before anything is resolved or connected.
 */
export function assertFetchableUrlShape(url: URL): void {
  if (!["http:", "https:"].includes(url.protocol)) {
    throw new BlockedFetchTargetError(
      `Unsupported protocol ${url.protocol} for ${url.toString()}`,
      { kind: "policy" },
    );
  }

  if (url.username || url.password) {
    throw new BlockedFetchTargetError(
      `URL carries embedded credentials: ${url.host}`,
      { kind: "policy" },
    );
  }
}

/**
 * Names that point at the local machine or a private network by convention.
 * A hostname matches when it equals one of these or ends in `.<name>`.
 */
const NON_PUBLIC_DOMAIN_NAMES = [
  "localhost",
  "local",
  "internal",
  "home.arpa",
  // Kubernetes service names (`<svc>.<ns>.svc`, `….svc.cluster.local`).
  "svc",
];

/**
 * Checks a URL a customer registers for us to call later: HTTPS, no embedded
 * credentials, and no host that names a loopback or private address. This is
 * the stored-URL check; it cannot see where a public hostname resolves, so the
 * request itself must still go through `fetchWithSsrfGuard`.
 */
export function assertPublicHttpsUrl(url: URL): void {
  assertFetchableUrlShape(url);

  if (url.protocol !== "https:") {
    throw new BlockedFetchTargetError(`URL must use HTTPS: ${url.toString()}`, {
      kind: "policy",
    });
  }

  // A trailing dot is the same DNS name (`localhost.`), so strip it first.
  const hostname = normalizeHostname(url.hostname).replace(/\.+$/, "");
  const isIpLiteral = /^[0-9.]+$/.test(hostname) || hostname.includes(":");
  // A single-label name resolves through the resolver's search domains —
  // inside a cluster that is a service name (`redis`, `kubernetes`).
  if (!isIpLiteral && !hostname.includes(".")) {
    throw new BlockedFetchTargetError(
      `URL host is not a public domain name: ${hostname}`,
      { kind: "policy" },
    );
  }
  if (
    NON_PUBLIC_DOMAIN_NAMES.some(
      (name) => hostname === name || hostname.endsWith(`.${name}`),
    )
  ) {
    throw new BlockedFetchTargetError(
      `URL host is not publicly routable: ${hostname}`,
      { kind: "policy" },
    );
  }

  // WHATWG URL parsing already rewrites numeric IPv4 forms (`0x7f.1`,
  // `2130706433`) to dotted decimal, so a plain pattern catches them.
  if (isIpLiteral && isBlockedIpAddress(hostname)) {
    throw new BlockedFetchTargetError(
      `URL host is not a public address: ${hostname}`,
      { kind: "policy" },
    );
  }
}
